// Ad-creative thumbnail helpers (Phase 3c) — PURE (no JSX, no DOM).
//
// The small bits of logic behind the ad-row thumbnail: what KIND of creative a row
// carries (video vs image vs none — drives the play badge + which placeholder icon)
// and whether to fall back to the neutral placeholder tile (no usable thumbnail).
// The Meta CDN URLs are short-lived, so the component ALSO degrades to the
// placeholder on an <img> load error — but that's a runtime signal the pure layer
// can't see; here we only decide from the data.

import type { ReportingCreative } from '../../types/reporting';

export type CreativeKind = 'video' | 'image' | 'none';

const nonBlank = (s: string | null | undefined): s is string =>
  typeof s === 'string' && s.trim() !== '';

// A creative's kind: VIDEO when it carries a video url (even if the thumbnail is
// missing — it's still a video); IMAGE when it has a thumbnail but no video; NONE
// when it has neither (or there's no creative at all). Drives the play badge (video)
// and the placeholder icon choice (video → film icon, else photo icon).
export function creativeKind(creative: ReportingCreative | null): CreativeKind {
  if (!creative) return 'none';
  if (nonBlank(creative.videoUrl)) return 'video';
  if (nonBlank(creative.thumbnailUrl)) return 'image';
  return 'none';
}

// Whether the inline tile should render the neutral placeholder (vs a real image):
// true when there's no creative, or no usable thumbnail url. This is what makes an
// expired-CDN / deleted-from-Meta ad look intentional rather than a broken image.
export function showPlaceholder(creative: ReportingCreative | null): boolean {
  return !creative || !nonBlank(creative.thumbnailUrl);
}

export interface CreativeLinkOut {
  href: string;
  label: string;
}

// The single "link out" a creative offers, in priority order: the destination
// link → the Instagram permalink → the video watch-page URL. The video_url is a
// Facebook watch PAGE (opens fine in a new tab even though it can't stream in a
// <video>), so it's the last resort that keeps a video-only ad — no thumbnail,
// no link, no permalink — reachable, labelled "View on Facebook". null when the
// creative offers nothing linkable (no dead link).
export function creativeLinkOut(creative: ReportingCreative | null): CreativeLinkOut | null {
  if (!creative) return null;
  if (nonBlank(creative.linkUrl)) return { href: creative.linkUrl, label: 'Open ad link' };
  if (nonBlank(creative.instagramPermalinkUrl)) {
    return { href: creative.instagramPermalinkUrl, label: 'Open ad link' };
  }
  if (nonBlank(creative.videoUrl)) return { href: creative.videoUrl, label: 'View on Facebook' };
  return null;
}
